import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { videoService } from "../../services/videoService";
import { Folder, Video } from "../../types";
import { getImageUrl } from "../../services/api";
import {
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  ArrowLeftIcon,
  EyeIcon,
  FolderIcon,
  DocumentIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";

const FolderVideos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // ... (rest of the component)
  const navigate = useNavigate();

  const [folder, setFolder] = useState<Folder | null>(null);
  const [subfolders, setSubfolders] = useState<Folder[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // Client-side pagination for videos?

  useEffect(() => {
    if (id) {
      fetchFolderContent(parseInt(id));
    }
  }, [id]);

  const fetchFolderContent = async (folderId: number) => {
    try {
      setIsLoading(true);
      const data = await videoService.getFolderContents(folderId);

      setFolder(data.folder);

      // Sort subfolders
      const sortedSubfolders = (data.folder.subfolders || []).sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );
      setSubfolders(sortedSubfolders);

      // Sort videos
      const sortedVideos = (data.folder.videos || []).sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );
      setVideos(sortedVideos);

      // Determine total pages if we were doing server-side, but getFolderContents returns all.
      // So we can do client-side pagination or just show all.
      // Showing all for simplicity as file explorers usually do.
    } catch (error) {
      console.error("Error fetching folder content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    if (folder?.parent_id) {
      navigate(`/folder/${folder.parent_id}`);
    } else {
      navigate("/dashboard");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">ກຳລັງໂຫຼດ...</p>
        </div>
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Folder not found
          </h3>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 sm:py-4">
      <div className="px-1 sm:px-2">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleBackClick}
              className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors duration-200 group bg-white px-3 py-1.5 rounded-lg shadow-sm hover:shadow-md"
            >
              <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="font-medium text-sm">
                {folder.parent_id ? "ກັບຄືນ" : "ໜ້າຫຼັກ"}
              </span>
            </button>
            <div className="flex items-center gap-2 mr-2">
              <FolderIcon className="h-5 w-5 text-purple-500" />
              <h5 className="text-md font-bold text-gray-900">{folder.name}</h5>
            </div>
          </div>
        </div>

        {/* Subfolders Section */}
        {subfolders.length > 0 && (
          <div className="mb-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {subfolders.map((subfolder) => (
                <Link
                  to={`/folder/${subfolder.id}`}
                  key={subfolder.id}
                  className="group relative bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 flex flex-col justify-center items-center h-20 sm:h-24 cursor-pointer hover:shadow-lg transform hover:-translate-y-1 hover:scale-105"
                >
                  <div className="w-full h-full flex items-center justify-center p-2 sm:p-3 relative bg-gradient-to-r from-purple-500 to-indigo-600">
                    <h3
                      className="text-sm sm:text-base font-bold text-white text-center truncate w-full px-2"
                      title={subfolder.name}
                    >
                      {subfolder.name}
                    </h3>

                    <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="bg-white bg-opacity-90 rounded-full p-1">
                        <FolderIcon className="h-4 w-4 text-indigo-600" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Videos Section */}
        {videos.length > 0 ? (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 px-1">
              ເນື້ອຫາ
            </h2>
            {/* Reduced Grid Gap: gap-2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4 pb-2">
              {videos.map((item) => {
                const isVideo = item.file_type === "video" || !item.file_type;
                const isPdf = item.file_type === "pdf";
                const isImage = item.file_type === "image";

                const handleFileClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  if (!item.cloudinary_url) return;

                  let fileUrl = getImageUrl(item.cloudinary_url);

                  // If it's a Cloudinary URL, force download by adding fl_attachment
                  if (
                    fileUrl.includes("res.cloudinary.com") &&
                    fileUrl.includes("/upload/")
                  ) {
                    fileUrl = fileUrl.replace(
                      "/upload/",
                      "/upload/fl_attachment/",
                    );
                  }

                  // Create a temporary link to trigger download
                  const link = document.createElement("a");
                  link.href = fileUrl;
                  link.setAttribute(
                    "download",
                    item.original_filename || item.title || "file",
                  );
                  link.target = "_blank";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                };

                return (
                  <div
                    key={item.id}
                    className="flex bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 h-24 sm:h-28"
                  >
                    {/* Image/Icon Column */}
                    <div className="w-28 sm:w-36 flex-shrink-0 relative bg-gray-100">
                      {isPdf || isImage ? (
                        <div
                          onClick={handleFileClick}
                          className="block h-full w-full cursor-pointer"
                        >
                          {/* For PDF/Image, show icon or thumbnail if available */}
                          {item.thumbnail_url &&
                          item.thumbnail_url.trim() !== "" ? (
                            <img
                              src={getImageUrl(item.thumbnail_url)}
                              alt={item.title}
                              className="h-full w-full object-cover object-center hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center p-4"
                              style={{
                                backgroundColor:
                                  item.thumbnail_color ||
                                  (isPdf ? "#EF4444" : "#10B981"), // Red for PDF, Green for Image
                              }}
                            >
                              {isPdf ? (
                                <DocumentIcon className="h-10 w-10 text-white opacity-90" />
                              ) : (
                                <PhotoIcon className="h-10 w-10 text-white opacity-90" />
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={`/videos/${item.id}`}
                          className="block h-full w-full"
                        >
                          {item.thumbnail_url &&
                          item.thumbnail_url.trim() !== "" ? (
                            <img
                              src={getImageUrl(item.thumbnail_url)}
                              alt={item.title}
                              className="h-full w-full object-cover object-center hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          ) : (
                            <div
                              className="w-full h-full flex items-center justify-center"
                              style={{
                                backgroundColor:
                                  item.thumbnail_color || "#3B82F6",
                              }}
                            >
                              <PlayIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white opacity-90" />
                            </div>
                          )}
                        </Link>
                      )}

                      {/* Duration Badge - Only for Videos */}
                      {isVideo && (
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-[0.6rem] font-medium px-1.5 py-0.5 rounded">
                          {videoService.formatDuration(item.duration)}
                        </div>
                      )}

                      {/* File Type Badge for non-videos */}
                      {!isVideo && (
                        <div className="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-[0.6rem] font-medium px-1.5 py-0.5 rounded uppercase">
                          {item.file_type || "FILE"}
                        </div>
                      )}
                    </div>

                    {/* Content Column */}
                    <div className="flex-1 p-2 sm:p-3 flex flex-col justify-between min-w-0">
                      <div>
                        {isPdf || isImage ? (
                          <div
                            onClick={handleFileClick}
                            className="cursor-pointer"
                          >
                            <h3 className="font-bold text-indigo-600 text-lg sm:text-xl leading-snug hover:text-indigo-800 line-clamp-2 mb-1 text-left">
                              {item.title}
                            </h3>
                          </div>
                        ) : (
                          <Link to={`/videos/${item.id}`}>
                            <h3 className="font-bold text-indigo-600 text-lg sm:text-xl leading-snug hover:text-indigo-800 line-clamp-2 mb-1 text-left">
                              {item.title}
                            </h3>
                          </Link>
                        )}

                        <div className="text-sm sm:text-base text-gray-500 text-left mt-1">
                          {item.description}
                        </div>
                      </div>

                      <div className="flex items-end justify-between mt-1">
                        {/* Status Badges */}
                        <div className="flex flex-col gap-1">
                          {/* Only show watched/completed logic for videos currently */}
                          {isVideo && (
                            <>
                              {item.completed ? (
                                <div className="inline-flex items-center text-green-700 bg-green-50 px-1.5 py-0.5 rounded-md text-[0.6rem] sm:text-[0.65rem] font-bold w-fit border border-green-100">
                                  <CheckCircleIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                                  ສຳເລັດ
                                </div>
                              ) : item.watched_duration &&
                                item.watched_duration > 0 ? (
                                <div className="inline-flex items-center text-yellow-700 bg-yellow-50 px-1.5 py-0.5 rounded-md text-[0.6rem] sm:text-[0.65rem] font-bold w-fit border border-yellow-100">
                                  <ClockIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                                  ກຳລັງຮຽນ
                                </div>
                              ) : (
                                <div className="inline-flex items-center text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded-md text-[0.6rem] sm:text-[0.65rem] font-medium w-fit">
                                  <PlayIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                                  ໃໝ່
                                </div>
                              )}
                            </>
                          )}
                          {!isVideo && (
                            <div className="inline-flex items-center text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-md text-[0.6rem] sm:text-[0.65rem] font-medium w-fit">
                              <DocumentIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                              ເອກະສານ
                            </div>
                          )}
                        </div>

                        {/* View Count (keep for all if applicable, or hide for files) */}
                        {/* Assuming views are tracked for files as well, keeping it */}
                        <div className="flex items-center text-[0.65rem] sm:text-[0.7rem] text-gray-400 font-medium">
                          <EyeIcon className="h-3 w-3 mr-1" />
                          <span className="whitespace-nowrap">
                            {item.views_count} ວິວ
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {videos.length === 0 && subfolders.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100 mx-auto max-w-2xl">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-50 rounded-full mb-6">
              <FolderIcon className="h-10 w-10 text-indigo-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">ຫວ່າງເປົ່າ</h3>
            <p className="text-gray-500 mb-8 max-w-sm mx-auto">
              ໂຟນເດີນີ້ບໍ່ມີເນື້ອຫາ
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderVideos;
